import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ShieldCheck,
  Building2,
  UserCheck,
  Mail,
  CheckCircle,
  AlertCircle,
  Lock,
  Loader2,
  LogOut,
  Clock,
  Briefcase,
  GraduationCap,
  BookOpen,
  Megaphone,
  Wallet
} from 'lucide-react';
import { toast } from 'sonner';
import AnnouncementsAdmin from '@/components/AnnouncementsAdmin';
import AdminProjects from '@/components/AdminProjects';
import FinanceSummary from '@/components/FinanceSummary';
import AdminUniversities from '@/components/AdminUniversities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPanel() {
  const [loading, setLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [experts, setExperts] = useState([]);
  const [industryUsers, setIndustryUsers] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  useEffect(() => {
    // Check for existing admin token
    const storedToken = localStorage.getItem('collabhive_admin_token');
    if (storedToken) {
      setToken(storedToken);
      setAuthenticated(true);
      fetchData(storedToken);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });
      
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('collabhive_admin_token', data.access_token);
        setToken(data.access_token);
        setAuthenticated(true);
        toast.success('Admin login successful');
        fetchData(data.access_token);
      } else {
        toast.error('Invalid admin credentials');
      }
    } catch (error) {
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('collabhive_admin_token');
    setToken(null);
    setAuthenticated(false);
    setExperts([]);
    setIndustryUsers([]);
    toast.success('Logged out successfully');
  };

  const fetchData = async (authToken) => {
    setDataLoading(true);
    
    try {
      const headers = { 'Authorization': `Bearer ${authToken}` };
      
      const [expertsRes, industryRes] = await Promise.all([
        fetch(`${API}/admin/experts`, { headers }),
        fetch(`${API}/admin/industry`, { headers })
      ]);
      
      if (expertsRes.ok) {
        const expertsData = await expertsRes.json();
        setExperts(expertsData.experts || []);
      }
      
      if (industryRes.ok) {
        const industryData = await industryRes.json();
        setIndustryUsers(industryData.industry_users || []);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setDataLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-zinc-900 border-zinc-800">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-white">Admin Panel</CardTitle>
            <CardDescription className="text-zinc-400">
              CollabHive Administration Access
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-zinc-300">Username</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter admin username"
                  value={credentials.username}
                  onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="admin-username"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-zinc-300">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter admin password"
                  value={credentials.password}
                  onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="admin-password-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full gap-2" 
                disabled={loading}
                data-testid="admin-login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Authenticating...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    Access Admin Panel
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="bg-zinc-900 border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-white">CollabHive Admin</h1>
              <p className="text-xs text-zinc-500">Administration Panel</p>
            </div>
          </div>
          
          <Button 
            variant="outline" 
            onClick={handleLogout}
            className="gap-2 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                  <UserCheck className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">{experts.length}</p>
                  <p className="text-sm text-zinc-500">Registered Experts</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-blue-500" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">{industryUsers.length}</p>
                  <p className="text-sm text-zinc-500">Industry Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Data Tables */}
        <Card className="bg-zinc-900 border-zinc-800">
          <Tabs defaultValue="experts">
            <CardHeader className="pb-0">
              <TabsList className="bg-zinc-800">
                <TabsTrigger value="experts" className="gap-2 data-[state=active]:bg-zinc-700">
                  <UserCheck className="w-4 h-4" />
                  Experts ({experts.length})
                </TabsTrigger>
                <TabsTrigger value="industry" className="gap-2 data-[state=active]:bg-zinc-700">
                  <Building2 className="w-4 h-4" />
                  Industry ({industryUsers.length})
                </TabsTrigger>
                <TabsTrigger value="announcements" className="gap-2 data-[state=active]:bg-zinc-700" data-testid="tab-announcements">
                  <Megaphone className="w-4 h-4" />
                  Opportunities
                </TabsTrigger>
                <TabsTrigger value="projects" className="gap-2 data-[state=active]:bg-zinc-700" data-testid="tab-projects">
                  <BookOpen className="w-4 h-4" />
                  Projects
                </TabsTrigger>
                <TabsTrigger value="universities" className="gap-2 data-[state=active]:bg-zinc-700" data-testid="tab-universities">
                  <GraduationCap className="w-4 h-4" />
                  Universities
                </TabsTrigger>
                <TabsTrigger value="finance" className="gap-2 data-[state=active]:bg-zinc-700" data-testid="tab-finance">
                  <Wallet className="w-4 h-4" />
                  Finance
                </TabsTrigger>
              </TabsList>
            </CardHeader>
            
            <CardContent className="pt-6">
              {dataLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  {/* Experts Tab */}
                  <TabsContent value="experts" className="mt-0">
                    {experts.length === 0 ? (
                      <div className="text-center py-12 text-zinc-500">
                        No experts registered yet
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="border-zinc-800 hover:bg-transparent">
                              <TableHead className="text-zinc-400">Name</TableHead>
                              <TableHead className="text-zinc-400">Email</TableHead>
                              <TableHead className="text-zinc-400">LinkedIn ID</TableHead>
                              <TableHead className="text-zinc-400">ORCID</TableHead>
                              <TableHead className="text-zinc-400">Status</TableHead>
                              <TableHead className="text-zinc-400">Qualifications</TableHead>
                              <TableHead className="text-zinc-400">Registered</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {experts.map((expert) => (
                              <TableRow key={expert.id} className="border-zinc-800 hover:bg-zinc-800/50">
                                <TableCell className="font-medium text-white">
                                  {expert.name || 'N/A'}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2 text-zinc-300">
                                    <Mail className="w-4 h-4 text-zinc-500" />
                                    {expert.email}
                                  </div>
                                </TableCell>
                                <TableCell className="text-zinc-400 font-mono text-xs">
                                  {expert.linkedin_id || 'N/A'}
                                </TableCell>
                                <TableCell className="text-zinc-400 font-mono text-xs">
                                  {expert.orcid_id || 'N/A'}
                                </TableCell>
                                <TableCell>
                                  {expert.is_approved ? (
                                    <Badge className="bg-green-500/10 text-green-400 hover:bg-green-500/20">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      Approved
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20">
                                      <AlertCircle className="w-3 h-3 mr-1" />
                                      Pending
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="flex gap-1">
                                    <Badge variant="outline" className={`text-xs ${expert.has_lab_access ? 'border-green-500/50 text-green-400' : 'border-zinc-700 text-zinc-500'}`}>
                                      Lab
                                    </Badge>
                                    <Badge variant="outline" className={`text-xs ${expert.connected_with_experts ? 'border-green-500/50 text-green-400' : 'border-zinc-700 text-zinc-500'}`}>
                                      Network
                                    </Badge>
                                    <Badge variant="outline" className={`text-xs ${expert.worked_with_industry ? 'border-green-500/50 text-green-400' : 'border-zinc-700 text-zinc-500'}`}>
                                      Industry
                                    </Badge>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2 text-zinc-400 text-sm">
                                    <Clock className="w-4 h-4" />
                                    {formatDate(expert.created_at)}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </TabsContent>
                  
                  {/* Industry Tab */}
                  <TabsContent value="industry" className="mt-0">
                    {industryUsers.length === 0 ? (
                      <div className="text-center py-12 text-zinc-500">
                        No industry users registered yet
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="border-zinc-800 hover:bg-transparent">
                              <TableHead className="text-zinc-400">Email</TableHead>
                              <TableHead className="text-zinc-400">Company</TableHead>
                              <TableHead className="text-zinc-400">User ID</TableHead>
                              <TableHead className="text-zinc-400">Registered</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {industryUsers.map((user) => (
                              <TableRow key={user.id} className="border-zinc-800 hover:bg-zinc-800/50">
                                <TableCell>
                                  <div className="flex items-center gap-2 text-zinc-300">
                                    <Mail className="w-4 h-4 text-zinc-500" />
                                    {user.email}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2 text-white">
                                    <Building2 className="w-4 h-4 text-zinc-500" />
                                    {user.company_name || <span className="text-zinc-500">Not specified</span>}
                                  </div>
                                </TableCell>
                                <TableCell className="text-zinc-400 font-mono text-xs">
                                  {user.id}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2 text-zinc-400 text-sm">
                                    <Clock className="w-4 h-4" />
                                    {formatDate(user.created_at)}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </TabsContent>
                </>
              )}
              <TabsContent value="announcements" className="mt-0">
                <AnnouncementsAdmin token={token} />
              </TabsContent>
              <TabsContent value="projects" className="mt-0">
                <AdminProjects token={token} />
              </TabsContent>
              <TabsContent value="universities" className="mt-0">
                <AdminUniversities token={token} API={API} />
              </TabsContent>
              <TabsContent value="finance" className="mt-0">
                <FinanceSummary API={API} token={token} defaultRole="admin" />
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>
      </main>
    </div>
  );
}
